import { compareAsc, format, fromUnixTime, getUnixTime, isSameDay, parse, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';

import useViewer from '@hooks/useViewer';
import LoadingArea from '@molecules/LoadingArea';
import TrusteeEventSessionListItem from './TrusteeEventSessionListItem';
import EventDayTabs from '../../../_molecules/EventDayTabs';

const dateFormatString = 'yyyy-MM-dd h:mma';

const areSessionDatesBeforeEventStartDate = (event, eventSessions) => {
    if (!event) return false;
    if (!event?.meeting_start_time) return false;
    const { meeting_start_time: eventStartTime } = event;

    if (!Array.isArray(eventSessions)) return false;
    if (!eventSessions.length) return false;
    if (!eventSessions[0]?.session_start_time) return false;
    const { session_start_time: firstSessionStartTimestamp } = eventSessions[0];

    const eventStartTimestamp = getUnixTime(eventStartTime);
    if (firstSessionStartTimestamp > eventStartTimestamp) return false;

    return true;
};

const mapTSFunctionToEESession = tsFunction => {
    const { id, title, begins_at: beginsAtISO, ends_at: endsAtISO, room, description, ...rest } = tsFunction;
    const beginDate = parseISO(beginsAtISO);
    const endDate = parseISO(endsAtISO);

    return {
        id,
        detail_title: title,
        begin_date: format(beginDate, 'yyyy-MM-dd'),
        begin_time: format(beginDate, 'h:mmaaa'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        end_time: format(endDate, 'h:mmaaa'),
        room,
        description,
        ...rest,
    };
};

const TrusteeEventSessionsList = ({
    eventFromAlgolia,
    eventFromEE,
    eventFromTS,
    registeredSessions,
    registrantClass,
}) => {
    const { viewer } = useViewer();
    const [eventStartDate, setEventStartDate] = useState(eventFromAlgolia?.meeting_start_time);
    const eventEndDate = useMemo(() => new Date(eventFromEE.end_timestamp), [eventFromEE]);
    const [activeDay, setActiveDay] = useState(eventStartDate);
    const [sessionsForDay, setSessionsForDay] = useState();

    useEffect(() => {
        if (areSessionDatesBeforeEventStartDate(eventFromAlgolia, eventFromEE.sessions)) {
            const startDate = fromUnixTime(eventFromEE.sessions[0].session_start_time);
            setEventStartDate(startDate);
            setActiveDay(startDate);
        }
    }, [eventFromAlgolia, eventFromEE]);

    useEffect(() => {
        if (!viewer) {
            return;
        }

        const functionsFromTS = eventFromTS.functions
            .filter(f => {
                // Exclude full program
                if (f.code === 'FP') {
                    return false;
                }

                const memberCommitteeIDs = viewer.activeCommitteesFull.map(c => c.committee_id);
                if (f.committees === null || f.committees.length === 0) {
                    return true;
                }

                if (f.committees.filter(id => memberCommitteeIDs.includes(id)).length > 0) {
                    return true;
                }

                return false;
            })
            .map(mapTSFunctionToEESession);

        const sessions = [...eventFromEE.sessions, ...functionsFromTS]
            .filter(session => isSameDay(parseISO(session.begin_date), activeDay))
            .sort((a, b) => {
                const dateABegin = parse(`${a.begin_date} ${a.begin_time}`, dateFormatString, new Date());
                const dateBBegin = parse(`${b.begin_date} ${b.begin_time}`, dateFormatString, new Date());

                const beginDateTimeComparison = compareAsc(dateABegin, dateBBegin);
                if (beginDateTimeComparison !== 0) {
                    return beginDateTimeComparison;
                }

                // If begin times are equal, compare end times
                const dateAEnd = parse(`${a.end_date} ${a.end_time}`, dateFormatString, new Date());
                const dateBEnd = parse(`${b.end_date} ${b.end_time}`, dateFormatString, new Date());
                return compareAsc(dateAEnd, dateBEnd);
            });

        setSessionsForDay(sessions);
    }, [eventFromEE, eventFromTS, activeDay, viewer]);

    if (!sessionsForDay) {
        return <LoadingArea />;
    }

    return (
        <EventDayTabs
            eventStartDate={eventStartDate}
            eventEndDate={eventEndDate}
            activeDay={activeDay}
            onDayChange={setActiveDay}
        >
            {sessionsForDay.map(session => {
                const beginDateString = `${session.begin_date} ${session.begin_time}`;
                const beginDateObject = parse(beginDateString, dateFormatString, new Date());
                const beginDateUnix = getUnixTime(beginDateObject);
                const endDateString = `${session.end_date} ${session.end_time}`;
                const endDateObject = parse(endDateString, dateFormatString, new Date());
                const endDateUnix = getUnixTime(endDateObject);

                return (
                    <TrusteeEventSessionListItem
                        fees={session.fees}
                        key={session.id}
                        id={session.id}
                        title={session.detail_title}
                        sessionStartUnix={beginDateUnix}
                        sessionEndUnix={endDateUnix}
                        sessionRoom={session.room}
                        description={session.description}
                        event={eventFromAlgolia}
                        isRegistered={registeredSessions.includes(session.id)}
                        registrantClass={registrantClass}
                    />
                );
            })}
        </EventDayTabs>
    );
};

TrusteeEventSessionsList.propTypes = {
    eventFromAlgolia: PropTypes.object.isRequired,
    eventFromEE: PropTypes.object.isRequired,
    eventFromTS: PropTypes.object.isRequired,
    registeredSessions: PropTypes.arrayOf(PropTypes.string).isRequired,
    registrantClass: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TrusteeEventSessionsList;
